import { useMemo, useState } from 'react';

import { Stack } from '@mui/material';
import { GridColDef } from '@mui/x-data-grid';
import dayjs, { Dayjs } from 'dayjs';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';
import useSWR from 'swr';

import { DataGrid, DataGridToolbar, useColDefs } from '~components';
import VectorImage from '~components/media/vectorImage';
import { queryParameters } from '~constants';
import { DataGridToolBarFilterType, StatusVariant, UserRoleEnum } from '~enums';
import DevicesService from '~features/devices/services/devicesService';
import LogsService from '~features/telemetry/services/logsService';
import useAuthorize from '~hooks/useAuthorize';
import useColumnVisibility from '~hooks/useColumnVisibility';
import { getHeartbeatDataColDefs } from '~interfaces/deviceModel';
import { getService } from '~services/serviceFactory';
import { Dimens } from '~themes';

import DeviceLog from '../../../../interfaces/deviceLog';

const deviceService = getService(DevicesService);
const logsService = getService(LogsService);

interface HeartbeatsDataGridProps {
	showExportButton?: boolean;
	deviceId: string;
	overwrittenClientId?: string;
	initialStartDate?: Dayjs;
	initialEndDate?: Dayjs;
	handleExportButtonClicked?: () => void;
}

const HeartbeatsDataGrid = ({
	showExportButton = false,
	deviceId,
	overwrittenClientId,
	initialStartDate,
	initialEndDate,
	handleExportButtonClicked,
}: HeartbeatsDataGridProps) => {
	const { t } = useTranslation();
	const columnVisibility = useColumnVisibility();
	const { authorize } = useAuthorize();
	const [searchParams] = useSearchParams();
	const [totalCount, setTotalCount] = useState<number>(0);
	const colDefs = useColDefs<DeviceLog>();

	// Data loading
	const {
		data: deviceData,
		error: _deviceError,
		isLoading: _deviceIsLoading,
	} = useSWR(
		deviceService.getDeviceByIdCacheKey(deviceId, overwrittenClientId),
		([_, id, orgId]) => (id ? deviceService.getDeviceById(id, orgId) : null),
		{ revalidateOnMount: false, suspense: false }
	);

	const { data, error, isLoading, isValidating } = useSWR(
		logsService.getDeviceLogsCacheKey(deviceId, searchParams),
		() => {
			const page = searchParams.get(queryParameters.Page) ?? '1';
			const pageSize = searchParams.get(queryParameters.PageSize) ?? '10';
			const startDate =
				searchParams.get(queryParameters.StartDate) ??
				dayjs().add(-7, 'day').startOf('day').toISOString();
			const endDate =
				searchParams.get(queryParameters.EndDate) ?? dayjs().endOf('day').toISOString();
			const lockState = searchParams.get(queryParameters.LockState) ?? undefined;
			const deviceState = searchParams.get(queryParameters.DeviceState) ?? undefined;

			return logsService.getDeviceLogs({
				page: page,
				pageSize: pageSize,
				deviceId: deviceId,
				startDate: startDate,
				endDate: endDate,
				lockState: lockState,
				deviceState: deviceState,
				overwrittenClientId: overwrittenClientId,
			});
		},
		{
			onSuccess: (data) => data?.total && setTotalCount(data.total),
			// refreshInterval:
			// (refreshRatesInSeconds.find((el) => el.key === selectedRefreshRateKey)?.value ?? 30) * 1000,
		}
	);

	// Col definition
	const heartbeatColDefs = useMemo(() => {
		return getHeartbeatDataColDefs(deviceData?.model).filter((value) =>
			authorize(value.roleFilters ?? [UserRoleEnum.AllowAnonymous])
		);
	}, [deviceData?.model]);

	const columns: GridColDef<DeviceLog>[] = useMemo(() => {
		return heartbeatColDefs
			.filter((value) => columnVisibility.isShown(value.field, value.defaultShown))
			.map((def) => colDefs.gridColDefFor(def));
	}, [heartbeatColDefs, columnVisibility]);

	const filters = [
		{
			type: DataGridToolBarFilterType.DateRange,
			initialStartDate: initialStartDate,
			initialEndDate: initialEndDate,
		},
		{
			type: DataGridToolBarFilterType.LockState,
		},
		{
			type: DataGridToolBarFilterType.DeviceState,
		},
	];

	const actionBarActions = [
		...(showExportButton ?
			[
				{
					id: 'export',
					label: t('export', { ns: 'actions' }),
					variant: StatusVariant.Brand,
					onClick: handleExportButtonClicked,
					icon: <VectorImage name='icon/export' />,
				},
			]
		:	[]),
	];

	return (
		<Stack spacing={Dimens.spacingXl}>
			<DataGridToolbar<DeviceLog>
				filters={filters}
				actions={actionBarActions}
				columns={heartbeatColDefs}
			/>
			<DataGrid
				persistenceKey='metrics'
				error={error != null}
				disableRowSelectionOnClick
				loading={isLoading || isValidating}
				columns={columns}
				rows={data?.results ?? []}
				rowCount={totalCount}
				paginationMode='server'
				paginationModel={{
					page: Number(searchParams.get(queryParameters.Page)),
					pageSize: Number(searchParams.get(queryParameters.PageSize)),
				}}
			/>
		</Stack>
	);
};

export default HeartbeatsDataGrid;
